import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Download, Printer, Mail, FileText, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function SectionSelectionDialog({ 
  open, 
  onOpenChange, 
  assessment, 
  onOutput,
  api
}) {
  const [outputMode, setOutputMode] = useState('full'); // 'full' or 'custom'
  const [outputType, setOutputType] = useState('download'); // 'download', 'print', 'email'
  const [selectedSections, setSelectedSections] = useState([]);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);

  // Fetch the actual sections from the assessment template when dialog opens
  useEffect(() => {
    if (open && assessment) {
      fetchAssessmentSections();
    }
  }, [open, assessment]);

  const fetchAssessmentSections = async () => {
    setLoadingSections(true);
    try {
      // Get template for this assessment type to get actual section headings
      const assessmentType = assessment?.assessment_type?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
      
      if (assessmentType && api) {
        try {
          const response = await api.get(`/assessments/templates/${assessmentType}`);
          const template = response.data;
          
          if (template && template.sections) {
            // Extract actual section names from the template
            const templateSections = template.sections.map((section, index) => ({
              id: `section_${index}`,
              name: section.name,
              required: index === 0 || section.name.toLowerCase().includes('signature') // First section and signatures usually required
            }));
            
            // Add header and signature sections if not present
            const hasHeader = templateSections.some(s => 
              s.name.toLowerCase().includes('general') || 
              s.name.toLowerCase().includes('patient') ||
              s.name.toLowerCase().includes('information')
            );
            
            const finalSections = [];
            
            // Always add patient info header
            finalSections.push({
              id: 'patient_info',
              name: 'Patient Information & Header',
              required: true
            });
            
            // Add all template sections
            templateSections.forEach(section => {
              finalSections.push(section);
            });
            
            // Add therapist signature at end if not present
            const hasSignature = templateSections.some(s => 
              s.name.toLowerCase().includes('signature') || 
              s.name.toLowerCase().includes('sign')
            );
            
            if (!hasSignature) {
              finalSections.push({
                id: 'therapist_signature',
                name: 'Therapist Signature & Date',
                required: true
              });
            }
            
            setSections(finalSections);
            setSelectedSections(finalSections.map(s => s.id));
            return;
          }
        } catch (err) {
          console.log('Could not fetch template, using assessment data directly');
        }
      }
      
      // Fallback: If we have the assessment data with sections/form_data, use those
      if (assessment?.form_data) {
        const formSections = Object.keys(assessment.form_data)
          .filter(key => typeof assessment.form_data[key] === 'object' && !Array.isArray(assessment.form_data[key]))
          .map((key, index) => ({
            id: key,
            name: formatSectionName(key),
            required: index === 0
          }));
        
        if (formSections.length > 0) {
          setSections(formSections);
          setSelectedSections(formSections.map(s => s.id));
          return;
        }
      }
      
      // Ultimate fallback: Generate sections based on assessment type
      const fallbackSections = getDefaultSectionsForType(assessment?.assessment_type);
      setSections(fallbackSections);
      setSelectedSections(fallbackSections.map(s => s.id));
      
    } catch (error) {
      console.error('Error fetching sections:', error);
      // Use fallback sections
      const fallbackSections = getDefaultSectionsForType(assessment?.assessment_type);
      setSections(fallbackSections);
      setSelectedSections(fallbackSections.map(s => s.id));
    } finally {
      setLoadingSections(false);
    }
  };

  // Format section names from camelCase or snake_case to Title Case
  const formatSectionName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Get default sections based on assessment type name
  const getDefaultSectionsForType = (assessmentType) => {
    const type = (assessmentType || '').toLowerCase();
    
    // OT Initial Assessment sections (from your actual template)
    if (type.includes('ot') || type.includes('occupational')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'general_info', name: 'General Information', required: false },
        { id: 'referral', name: 'Referral Information', required: false },
        { id: 'history', name: 'Medical History', required: false },
        { id: 'birth_history', name: 'Birth History', required: false },
        { id: 'developmental_history', name: 'Developmental History', required: false },
        { id: 'presenting_concerns', name: 'Presenting Concerns', required: false },
        { id: 'motor_skills', name: 'Motor Skills', required: false },
        { id: 'gross_motor', name: 'Gross Motor Skills', required: false },
        { id: 'fine_motor', name: 'Fine Motor Skills', required: false },
        { id: 'sensory_processing', name: 'Sensory Processing', required: false },
        { id: 'pre_academic', name: 'Pre-Academic Skills', required: false },
        { id: 'socialization', name: 'Socialization Skills', required: false },
        { id: 'cognitive_perceptual', name: 'Cognitive and Perceptual Skills', required: false },
        { id: 'adl', name: 'ADL/Occupational Performance', required: false },
        { id: 'goals', name: 'Goals and Impression', required: false },
        { id: 'therapist_signature', name: 'Therapist Signature & Date', required: true }
      ];
    }
    
    // Sensory Profile 2 sections
    if (type.includes('sensory')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'general_info', name: 'General Information', required: false },
        { id: 'sensory_processing', name: 'Sensory Processing Sections', required: false },
        { id: 'sensory_patterns', name: 'Sensory Patterns', required: false },
        { id: 'school_factors', name: 'School Factors', required: false },
        { id: 'quadrant_summary', name: 'Quadrant Summary', required: false },
        { id: 'interpretation', name: 'Interpretation & Analysis', required: false },
        { id: 'recommendations', name: 'Recommendations', required: false },
        { id: 'therapist_signature', name: 'Therapist Signature & Date', required: true }
      ];
    }
    
    // SLT Assessment sections
    if (type.includes('slt') || type.includes('speech') || type.includes('language')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'referral', name: 'Referral Information', required: false },
        { id: 'case_history', name: 'Case History', required: false },
        { id: 'receptive_language', name: 'Receptive Language', required: false },
        { id: 'expressive_language', name: 'Expressive Language', required: false },
        { id: 'speech_production', name: 'Speech Production', required: false },
        { id: 'pragmatics', name: 'Pragmatics & Social Communication', required: false },
        { id: 'fluency', name: 'Fluency Assessment', required: false },
        { id: 'voice', name: 'Voice Assessment', required: false },
        { id: 'feeding_swallowing', name: 'Feeding & Swallowing', required: false },
        { id: 'test_results', name: 'Test Results & Scores', required: false },
        { id: 'summary', name: 'Summary & Clinical Impression', required: false },
        { id: 'recommendations', name: 'Recommendations', required: false },
        { id: 'goals', name: 'Treatment Goals', required: false },
        { id: 'therapist_signature', name: 'Therapist Signature & Date', required: true }
      ];
    }
    
    // Psychological Assessment sections
    if (type.includes('psych')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'referral', name: 'Reason for Referral', required: false },
        { id: 'background', name: 'Background Information', required: false },
        { id: 'developmental_history', name: 'Developmental History', required: false },
        { id: 'behavioral_observations', name: 'Behavioral Observations', required: false },
        { id: 'cognitive_assessment', name: 'Cognitive Assessment', required: false },
        { id: 'emotional_functioning', name: 'Emotional & Social Functioning', required: false },
        { id: 'test_results', name: 'Test Results & Scores', required: false },
        { id: 'diagnostic_impressions', name: 'Diagnostic Impressions', required: false },
        { id: 'summary', name: 'Summary & Conclusions', required: false },
        { id: 'recommendations', name: 'Recommendations', required: false },
        { id: 'therapist_signature', name: 'Psychologist Signature & Date', required: true }
      ];
    }
    
    // Physiotherapy Assessment sections
    if (type.includes('pt') || type.includes('physio')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'referral', name: 'Referral Information', required: false },
        { id: 'history', name: 'Medical History', required: false },
        { id: 'presenting_complaint', name: 'Presenting Complaint', required: false },
        { id: 'physical_examination', name: 'Physical Examination', required: false },
        { id: 'range_of_motion', name: 'Range of Motion', required: false },
        { id: 'muscle_strength', name: 'Muscle Strength', required: false },
        { id: 'gait_analysis', name: 'Gait Analysis', required: false },
        { id: 'balance_coordination', name: 'Balance & Coordination', required: false },
        { id: 'functional_mobility', name: 'Functional Mobility', required: false },
        { id: 'assessment_findings', name: 'Assessment Findings', required: false },
        { id: 'goals', name: 'Treatment Goals', required: false },
        { id: 'treatment_plan', name: 'Treatment Plan', required: false },
        { id: 'therapist_signature', name: 'Physiotherapist Signature & Date', required: true }
      ];
    }
    
    // Nutritionist Assessment sections
    if (type.includes('nutri') || type.includes('diet')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'referral', name: 'Referral Information', required: false },
        { id: 'anthropometry', name: 'Anthropometric Measurements', required: false },
        { id: 'dietary_history', name: 'Dietary History', required: false },
        { id: 'food_intake', name: 'Food Intake Assessment', required: false },
        { id: 'feeding_skills', name: 'Feeding Skills', required: false },
        { id: 'nutritional_concerns', name: 'Nutritional Concerns', required: false },
        { id: 'assessment_findings', name: 'Assessment Findings', required: false },
        { id: 'recommendations', name: 'Dietary Recommendations', required: false },
        { id: 'meal_plan', name: 'Meal Plan', required: false },
        { id: 'therapist_signature', name: 'Nutritionist Signature & Date', required: true }
      ];
    }
    
    // Special Educator Assessment sections
    if (type.includes('special') || type.includes('educator') || type.includes('educational')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'referral', name: 'Referral Information', required: false },
        { id: 'educational_history', name: 'Educational History', required: false },
        { id: 'current_functioning', name: 'Current Academic Functioning', required: false },
        { id: 'reading_skills', name: 'Reading Skills', required: false },
        { id: 'writing_skills', name: 'Writing Skills', required: false },
        { id: 'math_skills', name: 'Mathematical Skills', required: false },
        { id: 'attention_concentration', name: 'Attention & Concentration', required: false },
        { id: 'learning_style', name: 'Learning Style', required: false },
        { id: 'behavioral_observations', name: 'Behavioral Observations', required: false },
        { id: 'assessment_findings', name: 'Assessment Findings', required: false },
        { id: 'recommendations', name: 'Educational Recommendations', required: false },
        { id: 'iep_goals', name: 'IEP Goals', required: false },
        { id: 'therapist_signature', name: 'Special Educator Signature & Date', required: true }
      ];
    }
    
    // Hand Therapy Assessment sections
    if (type.includes('hand')) {
      return [
        { id: 'patient_info', name: 'Patient Information & Header', required: true },
        { id: 'referral', name: 'Referral Information', required: false },
        { id: 'history', name: 'Medical History', required: false },
        { id: 'hand_dominance', name: 'Hand Dominance', required: false },
        { id: 'range_of_motion', name: 'Range of Motion', required: false },
        { id: 'grip_strength', name: 'Grip & Pinch Strength', required: false },
        { id: 'sensation', name: 'Sensation Assessment', required: false },
        { id: 'edema', name: 'Edema Assessment', required: false },
        { id: 'scar_assessment', name: 'Scar Assessment', required: false },
        { id: 'functional_use', name: 'Functional Hand Use', required: false },
        { id: 'assessment_findings', name: 'Assessment Findings', required: false },
        { id: 'goals', name: 'Treatment Goals', required: false },
        { id: 'treatment_plan', name: 'Treatment Plan', required: false },
        { id: 'therapist_signature', name: 'Hand Therapist Signature & Date', required: true }
      ];
    }
    
    // Generic/Default sections
    return [
      { id: 'patient_info', name: 'Patient Information & Header', required: true },
      { id: 'referral', name: 'Referral Information', required: false },
      { id: 'history', name: 'Medical & Developmental History', required: false },
      { id: 'presenting_concerns', name: 'Presenting Concerns', required: false },
      { id: 'observations', name: 'Clinical Observations', required: false },
      { id: 'assessment_findings', name: 'Assessment Findings', required: false },
      { id: 'test_results', name: 'Test Results & Scores', required: false },
      { id: 'analysis', name: 'Analysis & Interpretation', required: false },
      { id: 'summary', name: 'Summary & Conclusions', required: false },
      { id: 'recommendations', name: 'Recommendations', required: false },
      { id: 'goals', name: 'Treatment Goals', required: false },
      { id: 'therapist_signature', name: 'Therapist Signature & Date', required: true }
    ];
  };

  // Initialize selected sections when dialog opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setOutputMode('full');
      setEmailSubject(`Assessment Report - ${assessment?.patient_name || 'Patient'}`);
    }
    onOpenChange(isOpen);
  };

  const toggleSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section?.required) return; // Can't uncheck required sections
    
    setSelectedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const selectAll = () => {
    setSelectedSections(sections.map(s => s.id));
  };

  const selectNone = () => {
    // Keep only required sections
    setSelectedSections(sections.filter(s => s.required).map(s => s.id));
  };

  const handleOutput = async () => {
    setProcessing(true);
    
    try {
      const sectionsToInclude = outputMode === 'full' 
        ? sections.map(s => ({ id: s.id, name: s.name }))
        : sections.filter(s => selectedSections.includes(s.id)).map(s => ({ id: s.id, name: s.name }));
      
      const payload = {
        assessment_id: assessment?.id,
        output_type: outputType,
        output_mode: outputMode,
        sections: sectionsToInclude,
        section_names: sectionsToInclude.map(s => s.name),
        ...(outputType === 'email' && {
          email_to: emailTo,
          email_subject: emailSubject,
          email_message: emailMessage
        })
      };

      if (onOutput) {
        await onOutput(payload);
      } else {
        // Default behavior
        if (outputType === 'download') {
          toast.success(`Downloading ${outputMode === 'full' ? 'full' : 'custom'} assessment PDF with ${sectionsToInclude.length} sections`);
        } else if (outputType === 'print') {
          toast.success(`Printing ${sectionsToInclude.length} sections`);
          window.print();
        } else if (outputType === 'email') {
          toast.success(`Assessment (${sectionsToInclude.length} sections) will be emailed to ${emailTo}`);
        }
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Output failed:', error);
      toast.error(`Failed to ${outputType} assessment`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1e3a5f]" />
            Assessment Output Options
          </DialogTitle>
          {assessment && (
            <p className="text-sm text-slate-500 mt-1">
              {assessment.assessment_type} - {assessment.patient_name}
            </p>
          )}
        </DialogHeader>

        <Tabs value={outputType} onValueChange={setOutputType} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="download" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Download PDF
            </TabsTrigger>
            <TabsTrigger value="print" className="flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Print
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-hidden mt-4">
            {/* Output Mode Selection */}
            <div className="mb-4">
              <Label className="text-base font-semibold mb-2 block">Output Content</Label>
              <RadioGroup value={outputMode} onValueChange={setOutputMode} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="full" id="full" />
                  <Label htmlFor="full" className="cursor-pointer">Full Form (All Sections)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom" className="cursor-pointer">Custom Selection</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Section Selection (visible when custom mode) */}
            {outputMode === 'custom' && (
              <Card className="mb-4">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <Label className="text-sm font-medium">
                      Select Sections to Include
                      <span className="text-slate-500 font-normal ml-2">
                        ({assessment?.assessment_type || 'Assessment'})
                      </span>
                    </Label>
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={selectAll}>
                        Select All
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={selectNone}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  {loadingSections ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#1e3a5f]" />
                      <span className="ml-2 text-slate-500">Loading sections...</span>
                    </div>
                  ) : (
                    <ScrollArea className="h-[250px] pr-4">
                      <div className="space-y-2">
                        {sections.map((section, index) => (
                          <div 
                            key={section.id} 
                            className={`flex items-center space-x-2 p-2 rounded ${
                              selectedSections.includes(section.id) ? 'bg-blue-50' : 'hover:bg-slate-50'
                            }`}
                          >
                            <Checkbox
                              id={section.id}
                              checked={selectedSections.includes(section.id)}
                              onCheckedChange={() => toggleSection(section.id)}
                              disabled={section.required}
                            />
                            <label 
                              htmlFor={section.id} 
                              className={`cursor-pointer text-sm flex-1 ${section.required ? 'text-slate-500' : ''}`}
                            >
                              <span className="text-slate-400 mr-2">{index + 1}.</span>
                              <span className="font-medium">{section.name}</span>
                              {section.required && <span className="text-xs text-red-500 ml-2">(Required)</span>}
                            </label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                  
                  <p className="text-xs text-slate-500 mt-3 pt-2 border-t">
                    <strong>{selectedSections.length}</strong> of <strong>{sections.length}</strong> sections selected for output
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Email Options (visible when email mode) */}
            {outputType === 'email' && (
              <Card className="mb-4">
                <CardContent className="pt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email-to">Recipient Email *</Label>
                    <Input
                      id="email-to"
                      type="email"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="recipient@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-subject">Subject</Label>
                    <Input
                      id="email-subject"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Assessment Report"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-message">Message (Optional)</Label>
                    <Textarea
                      id="email-message"
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      placeholder="Add a personal message..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>
                    <strong>{outputMode === 'full' ? sections.length : selectedSections.length} sections</strong> will be 
                    {outputType === 'download' && ' downloaded as PDF'}
                    {outputType === 'print' && ' sent to printer'}
                    {outputType === 'email' && ` emailed to ${emailTo || '(enter email)'}`}
                  </span>
                </div>
                {outputMode === 'custom' && selectedSections.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500">
                    Including: {sections
                      .filter(s => selectedSections.includes(s.id))
                      .slice(0, 3)
                      .map(s => s.name)
                      .join(', ')}
                    {selectedSections.length > 3 && ` +${selectedSections.length - 3} more`}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleOutput} 
            disabled={processing || (outputType === 'email' && !emailTo) || loadingSections}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
          >
            {processing ? 'Processing...' : (
              <>
                {outputType === 'download' && <><Download className="w-4 h-4 mr-2" />Download PDF</>}
                {outputType === 'print' && <><Printer className="w-4 h-4 mr-2" />Print</>}
                {outputType === 'email' && <><Mail className="w-4 h-4 mr-2" />Send Email</>}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SectionSelectionDialog;
